import React, { useState } from "react";
import { Chip, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import MemoryIcon from "@material-ui/icons/Memory";
import CodeOutlinedIcon from "@material-ui/icons/CodeOutlined";
import LaunchIcon from "@material-ui/icons/Launch";

import Title from "../components/title";
import { getTranslate } from "../localization";

const useStyles = makeStyles((theme) => ({
  "@keyframes projectReveal": {
    from: { opacity: 0, transform: "translate3d(0,16px,0) scale(.985)" },
    to: { opacity: 1, transform: "translate3d(0,0,0) scale(1)" },
  },
  "@keyframes mediaScan": {
    "0%": { transform: "translateY(-155%)", opacity: 0 },
    "12%": { opacity: .16 },
    "50%": { opacity: .34 },
    "88%": { opacity: .14 },
    "100%": { transform: "translateY(620%)", opacity: 0 },
  },
  "@keyframes scanGrid": {
    from: { backgroundPosition: "0 0" },
    to: { backgroundPosition: "0 18px" },
  },
  root: {
    width: "100%",
    minHeight: "100vh",
    padding: "52px 44px 90px",
    direction: theme.direction,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 64px" },
    [theme.breakpoints.down("xs")]: { padding: "28px 16px 56px" },
  },
  inner: {
    width: "min(100%, 1320px)",
    margin: "0 auto",
  },
  lead: {
    maxWidth: 880,
    marginTop: -15,
    marginBottom: 42,
    color: "var(--text-secondary)",
    fontSize: 13,
    lineHeight: 1.95,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  section: { marginBottom: 66 },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 25,
    direction: theme.direction,
  },
  headingIconWrap: {
    width: 46,
    height: 46,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 14,
    color: "var(--accent)",
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  headingIcon: { fontSize: 27 },
  headingText: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(1.45rem,2.4vw,2rem)",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  featuredCard: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    borderRadius: 23,
    overflow: "hidden",
    background: "linear-gradient(150deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-md)",
    opacity: 0,
    animation: "$projectReveal .58s cubic-bezier(.2,.75,.2,1) forwards",
    animationDelay: "var(--project-delay, 0ms)",
    transition: "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
    "&:hover": {
      transform: "translateY(-7px)",
      borderColor: "var(--border-accent)",
      boxShadow: "var(--shadow-lg)",
    },
  },
  featuredMedia: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    minHeight: 190,
    overflow: "hidden",
    background: "#071329",
    borderBottom: "1px solid var(--border-soft)",
    "&::before": {
      content: '""',
      position: "absolute",
      zIndex: 6,
      inset: 0,
      pointerEvents: "none",
      opacity: .11,
      backgroundImage:
        "repeating-linear-gradient(180deg,rgba(93,199,255,.28) 0 1px,transparent 1px 9px)",
      animation: "$scanGrid 1.9s linear infinite",
      mixBlendMode: "screen",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      zIndex: 7,
      left: 0,
      right: 0,
      top: 0,
      height: "22%",
      pointerEvents: "none",
      background:
        "linear-gradient(180deg,transparent,rgba(39,177,255,.22),rgba(100,217,255,.38),transparent)",
      boxShadow: "0 0 24px rgba(22,139,255,.18)",
      animation: "$mediaScan 5.2s ease-in-out infinite 1s",
    },
  },
  featuredImage: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform .4s ease",
    "$featuredCard:hover &": { transform: "scale(1.03)" },
  },
  featuredFallback: {
    position: "absolute",
    zIndex: 1,
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 24,
    textAlign: "center",
    color: "var(--accent)",
    background:
      "radial-gradient(circle at 50% 35%,var(--glow-a),transparent 42%),linear-gradient(145deg,var(--surface-strong),var(--surface))",
  },
  fallbackMark: {
    fontSize: "clamp(1.8rem,4vw,3rem)",
    fontWeight: 900,
    letterSpacing: theme.direction === "rtl" ? 0 : ".06em",
  },
  fallbackDomain: {
    color: "var(--text-muted)",
    fontSize: 10,
    letterSpacing: theme.direction === "rtl" ? 0 : ".07em",
  },
  domainBadge: {
    position: "absolute",
    bottom: 12,
    left: theme.direction === "rtl" ? "auto" : 12,
    right: theme.direction === "rtl" ? 12 : "auto",
    zIndex: 9,
    maxWidth: "calc(100% - 24px)",
    padding: "6px 9px",
    borderRadius: 999,
    color: "#eaf6ff",
    fontSize: 9,
    fontWeight: 800,
    background: "rgba(4,10,20,.76)",
    border: "1px solid rgba(148,163,184,.24)",
    backdropFilter: "blur(10px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  featuredBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    padding: 22,
    [theme.breakpoints.down("xs")]: { padding: 18 },
  },
  type: {
    alignSelf: "flex-start",
    display: "inline-flex",
    maxWidth: "100%",
    padding: "5px 9px",
    marginBottom: 14,
    borderRadius: 999,
    color: "var(--accent)",
    fontSize: 9,
    fontWeight: 850,
    lineHeight: 1.5,
    letterSpacing: theme.direction === "rtl" ? 0 : ".07em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  featuredTitle: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(1.25rem,2.1vw,1.65rem)",
    lineHeight: 1.48,
    marginBottom: 11,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  featuredDesc: {
    color: "var(--text-secondary)",
    fontSize: 13,
    lineHeight: 1.92,
    marginBottom: 16,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 16,
  },
  tag: {
    color: "var(--text-soft)",
    height: 27,
    fontSize: 9,
    background: "var(--surface-soft)",
    border: "1px solid var(--border)",
  },
  metrics: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginTop: "auto",
  },
  metric: {
    padding: "6px 8px",
    borderRadius: 8,
    color: "var(--accent)",
    fontSize: 9,
    lineHeight: 1.5,
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  externalLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    width: "fit-content",
    marginTop: 18,
    color: "var(--accent)",
    fontSize: 11,
    fontWeight: 850,
    textDecoration: "none",
    transition: "color .2s ease, transform .2s ease",
    "&:hover": {
      color: "var(--accent-2)",
      transform: theme.direction === "rtl" ? "translateX(-2px)" : "translateX(2px)",
    },
  },
  linkIcon: { transform: theme.direction === "rtl" ? "scaleX(-1)" : "none" },
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    borderRadius: 20,
    overflow: "hidden",
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
    opacity: 0,
    animation: "$projectReveal .56s cubic-bezier(.2,.75,.2,1) forwards",
    animationDelay: "var(--project-delay, 0ms)",
    transition: "transform .23s ease, border-color .23s ease, box-shadow .23s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      borderColor: "var(--border-accent)",
      boxShadow: "var(--shadow-md)",
    },
  },
  imageWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    overflow: "hidden",
    background: "var(--page-bg-soft)",
  },
  image: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform .35s ease",
    "$card:hover &": { transform: "scale(1.03)" },
  },
  cardBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    padding: 18,
  },
  cardTitle: {
    color: "var(--text-primary)",
    fontWeight: 850,
    marginBottom: 7,
    lineHeight: 1.48,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  cardDesc: {
    color: "var(--text-secondary)",
    fontSize: 12,
    lineHeight: 1.82,
    margin: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  cardLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    width: "fit-content",
    marginTop: 15,
    color: "var(--accent)",
    fontSize: 10,
    fontWeight: 850,
  },
}));

function FeaturedProject({ item, classes, visitProject, index = 0 }) {
  const mediaCandidates = item.media || (item.image ? [item.image] : []);
  const [mediaIndex, setMediaIndex] = useState(0);
  const mediaSrc = mediaCandidates[mediaIndex];
  const showImage = Boolean(mediaSrc);

  const handleMediaError = () => {
    if (mediaIndex < mediaCandidates.length - 1) {
      setMediaIndex((value) => value + 1);
    } else {
      setMediaIndex(mediaCandidates.length);
    }
  };

  return (
    <article className={classes.featuredCard} style={{ "--project-delay": `${index * 90}ms` }}>
      <div className={classes.featuredMedia}>
        <>
          <div className={classes.featuredFallback}>
            <div className={classes.fallbackMark}>{item.visualLabel}</div>
            <div className={classes.fallbackDomain}>{item.domain}</div>
          </div>

          {showImage ? (
            <img
              src={mediaSrc}
              alt={item.imageAlt || item.title}
              className={classes.featuredImage}
              loading="lazy"
              decoding="async"
              onError={handleMediaError}
            />
          ) : null}
        </>

        {item.domain ? <div className={classes.domainBadge}>{item.domain}</div> : null}
      </div>

      <div className={classes.featuredBody}>
        <div className={classes.type}>{item.type}</div>
        <Typography component="h3" className={classes.featuredTitle}>{item.title}</Typography>
        <div className={classes.featuredDesc}>{item.desc}</div>

        <div className={classes.tags}>
          {item.tags.map((tag) => (
            <Chip key={tag} label={tag} variant="outlined" className={classes.tag} />
          ))}
        </div>

        <div className={classes.metrics}>
          {item.metrics.map((metric) => (
            <span key={metric} className={classes.metric}>{metric}</span>
          ))}
        </div>

        {item.href ? (
          <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.externalLink}>
            <span>{visitProject}</span><LaunchIcon className={classes.linkIcon} fontSize="small" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function ProjectCard({ item, classes, visitProject, index = 0 }) {
  return (
    <article className={classes.card} style={{ "--project-delay": `${index * 70}ms` }}>
      <div className={classes.imageWrap}>
        <img src={item.image} alt={item.title} className={classes.image} loading="lazy" />
      </div>
      <div className={classes.cardBody}>
        <Typography component="h3" variant="h5" className={classes.cardTitle}>{item.title}</Typography>
        <p className={classes.cardDesc}>{item.desc}</p>
        {item.href ? (
          <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.cardLink}>
            <span>{visitProject}</span><LaunchIcon className={classes.linkIcon} fontSize="small" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

export default function PortfolioScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  return (
    <div className={`${classes.root} modern-page`}>
      <div className={classes.inner}>
        <Title title={translate.portfolio} eyebrow={translate.portfolioEyebrow} />
        <div className={classes.lead}>{translate.portfolioLead}</div>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><WorkOutlineIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.featuredWork}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.featuredProjects.map((item, index) => (
              <Grid item xs={12} md={6} xl={4} lg={4} key={item.id}>
                <FeaturedProject
                  item={item}
                  classes={classes}
                  visitProject={translate.visitProject}
                  index={index}
                />
              </Grid>
            ))}
          </Grid>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><MemoryIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.selectedAIProjects}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.portfoliosmlList.map((item, index) => (
              <Grid item xs={12} md={6} key={item.id}>
                <ProjectCard item={item} classes={classes} visitProject={translate.visitProject} index={index} />
              </Grid>
            ))}
          </Grid>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><CodeOutlinedIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.frontend}</Typography>
          </div>

          <Grid container spacing={3}>
            {translate.portfoliosList.map((item, index) => (
              <Grid item xs={12} md={6} lg={4} key={item.id}>
                <ProjectCard item={item} classes={classes} visitProject={translate.visitProject} index={index} />
              </Grid>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  );
}
